import { useLocation, useNavigate } from "react-router-dom";
import { useSharedStatus } from "../lib/StatusContext";
import { askUrl, serviceUrl } from "../lib/links";
import { useHashTarget } from "../lib/motion";
import { TopbarSettings } from "../components/TopbarSettings";
import {
  HostRow,
  Meter,
  PageSkeleton,
  Row,
  Section,
  ServiceTile,
  SI,
} from "../components/status/atoms";

export function StackView() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const flashing = useHashTarget(hash);
  const { data, error, loading, reload } = useSharedStatus();
  const ask = (q: string) => navigate(askUrl(q));

  if (loading && !data) return <PageSkeleton title="Stack" />;

  const services = [...(data?.services ?? [])].sort(
    (a, b) => Number(b.role === "playback") - Number(a.role === "playback"),
  );
  const staleOf = (label: string) =>
    data?.instances.some(
      (i) =>
        (i.state === "drift" || i.state === "uhd-missing") &&
        /4k/i.test(label) &&
        label.toLowerCase().includes(i.app.toLowerCase()),
    );

  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">Stack</h1>
        <div className="topbar__right">
          <button
            type="button"
            className={`btn btn--ghost btn--icon ${loading ? "is-spinning" : ""}`}
            onClick={() => void reload()}
            aria-label="Refresh"
          >
            <svg {...SI} width="19" height="19">
              <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
            </svg>
          </button>
          <TopbarSettings />
        </div>
      </header>

      <div className="page-body">
        <div className="page-inner stack stagger">
          {error && <div className="banner banner--fault">Could not reach the console API: {error}</div>}

          <p className="page-lede">
            Storage, services, and hardware. Everything here opens the real thing.
          </p>

          <Section id="storage" title="Storage" flashing={flashing}>
            <div className="vols">
              {data?.volumes.map((v) => (
                <Meter
                  key={v.id}
                  v={v}
                  onAsk={() =>
                    ask(
                      `${v.label} is at ${v.usedPct}% used with ${v.freeText} free. What is taking up the space, and what is safe to clear?`,
                    )
                  }
                />
              ))}
              {!data?.volumes.length && <div className="empty">No volumes configured</div>}
            </div>
          </Section>

          <Section id="services" title="Services" flashing={flashing}>
            {services.map((s) => (
              <Row
                key={s.id}
                href={serviceUrl(s.url, s.role === "playback" ? "/web" : "")}
                title={`Open ${s.label}`}
              >
                <ServiceTile id={s.id} label={s.label} role={s.role} ok={s.ok} />
                <div className="grow">
                  <div className="row">
                    <strong>{s.label}</strong>
                    {staleOf(s.label) && <span className="pill pill--warn">stale</span>}
                  </div>
                  <div className="section__sub mono truncate">{s.detail}</div>
                </div>
              </Row>
            ))}
          </Section>

          <Section id="fleet" title="Hardware" flashing={flashing}>
            {data?.fleet.map((h) => (
              <HostRow
                key={h.host}
                h={h}
                onAsk={() => ask(`Is ${h.name} (${h.host}) healthy? It reports: ${h.detail}.`)}
              />
            ))}
          </Section>

          {data?.recent.length ? (
            <Section id="fixer" title="Import fixer" flashing={flashing}>
              <div className="loglines">
                {data.recent.map((l, i) => (
                  <button
                    key={i}
                    type="button"
                    className="logline logline--go"
                    onClick={() => ask(`The import fixer logged: "${l.text}". What does that mean and what should I do?`)}
                    title="Ask about this line"
                  >
                    <time>{l.time}</time>
                    <span>{l.text}</span>
                  </button>
                ))}
              </div>
            </Section>
          ) : null}

          {data && (
            <div className="stamp">
              <span className="dot dot--ok dot--live" />
              Updated {new Date(data.generatedAt).toLocaleTimeString()} · {data.mode}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
